import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  isDevMode,
  NgZone,
  OnInit,
  ViewChild,
} from "@angular/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { ActivatedRoute, Router } from "@angular/router";
import { PluginCardData } from "@bitwarden/web-vault/app/components/plugin-card/plugin-card.component";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { ToolBadgeService } from "./tool-badge.service";
import { ToolPrefsService } from "./tool-prefs.service";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { PLUGIN_CATALOG } from "@bitwarden/web-vault/app/models/data/plugin-catalog";
import {
  BUNDLE_PRICE_LABEL,
  GROCERY_PLUGIN_NAME,
  PluginConfigService,
  RECURRING_DETECTOR_PLUGIN_ID,
  TRANSFER_CONVERSION_PLUGIN_NAME,
  WEALTH_COMPARISON_PLUGIN_NAME,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { FiskilApi } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.api";
import { PlaidApi } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.api";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { GlossExtensionService } from "@bitwarden/web-vault/app/services/extension/gloss-extension.service";
import { PlatformUtilsService } from "@vault/common/abstractions/platformUtils.service";
import { BreakpointService } from "@bitwarden/web-vault/app/shared/breakpoints";

/** What the home search found, grouped the way the results page lists it. */
export interface HomeSearchResults {
  query: string;
  tools: (ToolTile & { plugin: PluginCardData })[];
  plugins: PluginCardData[];
  notInstalled: PluginCardData[];
  total: number;
  kind: "none" | "tools" | "plugins" | "notinst" | "mixed";
  /** i18n key for the grey text after the result count. */
  subKey: string;
  /** i18n key for the one-line "nothing else matched" note, if any. */
  noteKey?: string;
}

/** A launcher tile in the "Your tools" row. */
export interface ToolTile {
  /** Plugin id this tile launches (also its PLUGIN_CATALOG id). */
  id: string;
  labelKey: string;
  /** Literal label (dev mock tiles only); real tiles use the translated `labelKey`. */
  label?: string;
  icon: string;
}

/**
 * Tools shown on the Tools & Plugins home. Only these three are surfaced for now;
 * the rest of the catalog is reachable through the "Add new" store view.
 */
export const TOOL_TILES: ToolTile[] = [
  { id: RECURRING_DETECTOR_PLUGIN_ID, labelKey: "toolRecurringDetection", icon: "repeat" },
  { id: WEALTH_COMPARISON_PLUGIN_NAME, labelKey: "toolWealthComparison", icon: "bar-chart" },
  { id: GROCERY_PLUGIN_NAME, labelKey: "toolGroceryComparison", icon: "grocery" },
];

const ALL_CATEGORIES = "All categories";
/** Columns assumed until the grid has been measured (and where ResizeObserver is unavailable). */
const DEFAULT_TOOL_COLUMNS = 8;
/** Tile width (134px) plus the horizontal gap (8px) of the tools grid. */
const TOOL_COLUMN_WIDTH = 142;
const TOOL_GRID_GAP = 8;
/** Phone (< 640px, Tailwind `sm`): four tiles per row, one row until "Show all". */
const PHONE_COLLAPSED_TOOL_LIMIT = 4;
/**
 * Dev-only layout aid: `localStorage.setItem("plugins.mockTools", "14")` then reload adds that
 * many placeholder tiles to Your tools (never in a production build). They open nothing.
 */
const MOCK_TOOLS_KEY = "plugins.mockTools";
const MOCK_TOOL_NAMES: [string, string][] = [
  ["Calendar", "calendar"],
  ["Bill Split", "coins-swap"],
  ["Price Watch", "bar-chart"],
  ["Monthly Summary", "bar-chart"],
  ["Receipts", "receipt"],
  ["Net Worth", "wallet"],
  ["Statement Import", "file-upload"],
  ["FX Rates", "dollar"],
  ["Super Tracker", "bank"],
  ["Savings Goals", "piggy-bank"],
  ["Debt Planner", "coins"],
  ["Travel Budget", "route"],
  ["Subscriptions", "card"],
  ["Cash Flow Forecast", "pie-chart"],
  ["Loan Tracker", "bank-account"],
  ["Rent Tracker", "home"],
  ["Tax Companion", "document"],
  ["Offset Optimiser", "deduction"],
  ["Crypto Tax", "coins-hand"],
  ["Term Deposit Watch", "clock"],
  ["Spending Insights", "pie-chart"],
  ["Energy Bills", "lightning"],
  ["Invoice Tracker", "receipt2"],
  ["Warranty Vault", "shield"],
  ["Gift Planner", "gift"],
  ["Work Expenses", "wallet"],
  ["Charity Giving", "heart-on"],
  ["Insurance Review", "shield-outline"],
  ["Car Costs", "route"],
  ["Pet Budget", "smile"],
];

@Component({
  selector: "app-plugins",
  templateUrl: "./plugins.component.html",
})
export class PluginsComponent implements OnInit {
  private dialogFactory: DialogFactory = new DialogFactory();
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private pluginConfigService = inject(PluginConfigService);
  private fiskilApi = inject(FiskilApi);
  private plaidApi = inject(PlaidApi);
  private log = inject(LogService);
  private i18nService = inject(I18nService);
  private roleAccessService = inject(RoleAccessService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private extensionService = inject(GlossExtensionService);
  private platformUtils = inject(PlatformUtilsService);

  /** True on the native Capacitor iOS/Android apps, where the browser extension can't be installed. */
  private get isNativeMobile(): boolean {
    return this.platformUtils.isMobileDevice();
  }

  /** The tools grid lives in the "home" view, so it comes and goes; (re)attach the width watcher. */
  @ViewChild("toolsGrid") set toolsGrid(grid: ElementRef<HTMLElement> | undefined) {
    this.toolsGridObserver?.disconnect();
    this.toolsGridObserver = undefined;
    if (!grid || typeof ResizeObserver === "undefined") {
      return;
    }
    // ResizeObserver callbacks aren't zone-patched in this app; re-enter the zone so the new
    // column count is picked up by change detection.
    this.toolsGridObserver = new ResizeObserver(([entry]) =>
      this.ngZone.run(() => {
        const columns = Math.max(
          1,
          Math.floor((entry.contentRect.width + TOOL_GRID_GAP) / TOOL_COLUMN_WIDTH),
        );
        if (columns !== this.toolColumns) {
          this.toolColumns = columns;
          this.cdr.markForCheck();
        }
      }),
    );
    this.toolsGridObserver.observe(grid.nativeElement);
  }

  /** Reactive "is the Gloss Vault extension installed" flag, for its card badge. */
  private extensionInstalled = toSignal(this.extensionService.installedChanges(), {
    initialValue: this.extensionService.isInstalled(),
  });

  /** "home" = Tools & Plugins landing (design 2.1); "store" = browse/install every plugin. */
  view: "home" | "store" = "home";
  /** Search box on the home view; searches your tools, your plugins and the rest of Add new. */
  homeSearchQuery = "";
  readonly toolTiles = TOOL_TILES;
  readonly allCategories = ALL_CATEGORIES;
  readonly toolPrefs = inject(ToolPrefsService);
  private stateService = inject(StateService, { optional: true });
  /**
   * True on a phone-sized viewport; drives the few layout choices CSS alone can't make. Shares the
   * `sm` query with Tailwind (639.98px) so JS and the `sm:` classes flip on the same pixel.
   */
  readonly isPhone = inject(BreakpointService).isSmOrSmaller;
  /** Which of the phone catalogue's filter chips is open (Type / Category / Price). */
  phoneFilter: "type" | "category" | "price" | null = null;
  readonly phoneFilterChips = [
    { id: "type" as const, labelKey: "pluginsFilterTypeChip" },
    { id: "category" as const, labelKey: "pluginsFilterCategoryChip" },
    { id: "price" as const, labelKey: "pluginsFilterPriceChip" },
  ];
  private toolBadges = inject(ToolBadgeService);
  /** "Show all N" expands the tool grid past two rows in place. */
  toolsExpanded = false;
  /** How many tiles fit on a row; the collapsed grid shows two rows. Follows the grid's width. */
  private toolColumns = DEFAULT_TOOL_COLUMNS;
  private toolsGridObserver?: ResizeObserver;
  /** Spoken to screen readers after a keyboard reorder. */
  toolReorderAnnouncement = "";
  /** False until this user's saved arrangement is loaded, so tiles don't visibly reshuffle. */
  toolPrefsReady = this.stateService === null;

  selectedTab: "store" | "my-plugins" | "wishlist" = "store";
  searchQuery = "";

  readonly countryOptions = [
    { id: "australia", label: this.i18nService.t("australia") },
    { id: "hong-kong", label: this.i18nService.t("hongkong") },
    { id: "united-states", label: this.i18nService.t("unitedStates") },
  ];

  readonly bundleOptions = [
    { id: "free", label: this.i18nService.t("free") },
    { id: "aussie-bank-bundle", label: this.i18nService.t("aussieBankBundle") },
  ];

  // Every option starts checked, so the Store opens showing all plugins. A group that is fully
  // checked is "not filtering" — see isNarrowed(). A group with nothing checked matches nothing
  // — see noFilterOptionSelected.
  activeTypes: string[] = [];
  activeCategory = ALL_CATEGORIES;
  activeCountries: string[] = this.countryOptions.map((o) => o.id);
  activeBundles: string[] = this.bundleOptions.map((o) => o.id);

  /** A filter group narrows the list only when some, but not all, of its options are checked.
   * (A group with none checked is handled earlier by noFilterOptionSelected.) */
  private isNarrowed(selected: string[], options: { id: string }[]): boolean {
    return !options.every((o) => selected.includes(o.id));
  }

  private get countriesNarrowed(): boolean {
    return this.isNarrowed(this.activeCountries, this.countryOptions);
  }

  private get bundlesNarrowed(): boolean {
    return this.isNarrowed(this.activeBundles, this.bundleOptions);
  }

  /** Applied filter has a group with no option checked, so nothing can match: every plugin has
   * a country and a price, and a plugin must satisfy both groups. */
  get noFilterOptionSelected(): boolean {
    return this.activeCountries.length === 0 || this.activeBundles.length === 0;
  }

  readonly typeOptions = [
    { id: "Tools", label: this.i18nService.t("pluginTypeTools") },
    { id: "Plugins", label: this.i18nService.t("pluginTypePlugins") },
  ];

  /** Filters currently narrowing the catalogue (category counts only when not "all"). */
  get activeFilterCount(): number {
    return (
      this.activeTypes.length +
      (this.countriesNarrowed ? this.activeCountries.length : 0) +
      (this.bundlesNarrowed ? this.activeBundles.length : 0) +
      (this.activeCategory === ALL_CATEGORIES ? 0 : 1)
    );
  }

  /** "Clear all" is live whenever the list is narrowed, including a group left with nothing checked. */
  get canClearFilters(): boolean {
    return this.activeFilterCount > 0 || this.noFilterOptionSelected;
  }

  /** A "tool" launches from Your tools; everything else in the catalogue is a plugin. */
  pluginKind(plugin: PluginCardData): "Tools" | "Plugins" {
    return plugin.type === "Tools" || this.toolTiles.some((t) => t.id === plugin.id)
      ? "Tools"
      : "Plugins";
  }

  /** Catalogue entries this user can see, before any tab/search/filter is applied. */
  private get catalogue(): PluginCardData[] {
    return this.plugins.filter(
      (plugin) => !plugin.isBetaAccess || this.roleAccessService.isBetaUser(),
    );
  }

  /**
   * What Add tools & plugins lists: installed plugins live in Tools & Plugins (Manage), so they
   * are left out here; installed tools stay, with an Open button.
   */
  private get browsable(): PluginCardData[] {
    return this.catalogue.filter((p) => !this.isInstalledPlugin(p));
  }

  private isInstalledPlugin(plugin: PluginCardData): boolean {
    return this.pluginKind(plugin) === "Plugins" && this.isPluginInstalled(plugin.id);
  }

  /** How many installed plugins the list leaves out (for the "aren't listed here" note). */
  get hiddenInstalledPluginCount(): number {
    return this.catalogue.filter((p) => this.isInstalledPlugin(p)).length;
  }

  /** Show the note when nothing narrows the list in a way that would explain the omission. */
  get showInstalledNote(): boolean {
    const onlyPluginsType = this.activeTypes.length === 1 && this.activeTypes[0] === "Plugins";
    return (
      this.selectedTab === "store" &&
      !this.searchQuery.trim() &&
      this.hiddenInstalledPluginCount > 0 &&
      this.activeCategory === ALL_CATEGORIES &&
      !this.bundlesNarrowed &&
      !this.countriesNarrowed &&
      (this.activeTypes.length === 0 || onlyPluginsType)
    );
  }

  /** "Open" on an installed tool launches it; anything else installed opens its Manage panel. */
  onCatalogueAction(plugin: PluginCardData, event: Event): void {
    const tile = this.visibleToolTiles.find((t) => t.id === plugin.id);
    if (tile && this.isPluginInstalled(plugin.id)) {
      event.stopPropagation();
      this.openTool(tile);
      return;
    }
    this.openFromCard(plugin, event);
  }

  get savedCount(): number {
    return this.browsable.filter((p) => p.isLiked).length;
  }

  /** Everything in the catalogue (the page subtitle); `browsableCount` is what the list can show. */
  get totalCount(): number {
    return this.catalogue.length;
  }

  get browsableCount(): number {
    return this.browsable.length;
  }

  typeCount(id: string): number {
    return this.browsable.filter((p) => this.pluginKind(p) === id).length;
  }

  get categoryOptions(): { id: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const p of this.browsable) {
      const key = p.type ?? "Other";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [
      { id: ALL_CATEGORIES, count: this.browsable.length },
      ...[...counts].map(([id, count]) => ({ id, count })),
    ];
  }

  priceCount(id: string): number {
    return this.browsable.filter((p) => this.matchesBundle(p, [id])).length;
  }

  countryCount(id: string): number {
    return this.browsable.filter((p) => (p.countries ?? []).includes(id)).length;
  }

  private matchesBundle(plugin: PluginCardData, ids: string[]): boolean {
    return (
      (ids.includes("free") && plugin.price === "Free") ||
      (ids.includes("aussie-bank-bundle") && plugin.price === BUNDLE_PRICE_LABEL)
    );
  }

  private toggleIn(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  // Filters apply immediately (no pending/Apply step): the sidebar is always visible.
  toggleType(id: string): void {
    this.activeTypes = this.toggleIn(this.activeTypes, id);
  }

  toggleCountry(id: string): void {
    this.activeCountries = this.toggleIn(this.activeCountries, id);
  }

  toggleBundle(id: string): void {
    this.activeBundles = this.toggleIn(this.activeBundles, id);
  }

  setCategory(id: string): void {
    this.activeCategory = id;
  }

  /** One row of the Filters card: Category is a single pick, the other groups are multi-select. */
  toggleOption(group: "type" | "category" | "bundle" | "country", id: string): void {
    switch (group) {
      case "type":
        return this.toggleType(id);
      case "category":
        return this.setCategory(id);
      case "bundle":
        return this.toggleBundle(id);
      case "country":
        return this.toggleCountry(id);
    }
  }

  // Back to the opening state: no type or category, every country and bundle checked (so nothing
  // is narrowed). Leaves the panel open, and the filter applies as you toggle.
  clearFilter(): void {
    this.activeTypes = [];
    this.activeCategory = ALL_CATEGORIES;
    this.activeCountries = this.countryOptions.map((o) => o.id);
    this.activeBundles = this.bundleOptions.map((o) => o.id);
  }

  /** Price chip text; the long bundle label is shortened so it never truncates. */
  priceLabel(plugin: PluginCardData): string {
    return plugin.price === BUNDLE_PRICE_LABEL
      ? this.i18nService.t("pluginsInYourBundle")
      : (plugin.price ?? "");
  }

  /** Two-letter monogram for a plugin's logo tile. */
  monogram(plugin: PluginCardData): string {
    const words = plugin.title
      .replace(/[^\p{L}\p{N} ]/gu, "")
      .split(" ")
      .filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[1][0] : plugin.title.slice(0, 2)).toUpperCase();
  }

  /** "Tools · Finance · GLOSS Vault" style meta line on a catalogue card. */
  pluginMeta(plugin: PluginCardData): string {
    const kind = this.pluginKind(plugin).slice(0, -1);
    // The catalogue's "Tools" category would just repeat the kind ("Tool · Tools · …").
    const category = plugin.type === "Tools" ? undefined : plugin.type;
    return [kind, category, plugin.author.replace(/^By /, "")].filter(Boolean).join(" · ");
  }

  openFromCard(plugin: PluginCardData, event?: Event): void {
    event?.stopPropagation();
    this.openPluginDetails(plugin);
  }

  /**
   * Keyboard activation for a `role="button"` card. Ignores keys that bubbled up from a button
   * inside the card, which already handles its own activation (else the dialog opens twice).
   */
  onCardKeydown(plugin: PluginCardData, event: Event): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    // Space would otherwise scroll the page.
    event.preventDefault();
    this.openPluginDetails(plugin);
  }

  toggleSaved(plugin: PluginCardData, event: Event): void {
    event.stopPropagation();
    this.onPluginLikeToggle({ id: plugin.id, isLiked: !plugin.isLiked });
  }

  ngOnInit(): void {
    void this.toolBadges.refresh();
    // Tool order/pins are saved per user, so one account's arrangement doesn't show for another.
    void this.stateService
      ?.getUserId()
      .then((userId) => this.toolPrefs.useUser(userId))
      .catch((error) => {
        this.log.error("Failed to load tool preferences for the user: " + JSON.stringify(error));
        // Don't leave a previous user's arrangement showing; fall back to the unscoped one.
        this.toolPrefs.useUser(undefined);
      })
      .finally(() => {
        this.toolPrefsReady = true;
        this.cdr.markForCheck();
      });
    this.destroyRef.onDestroy(() => this.toolsGridObserver?.disconnect());
    void this.syncFiskilPluginConfigFromActiveConsent();
    void this.syncPlaidPluginConfigFromActiveItems();
    // Re-ping in case the extension was installed since app load.
    void this.extensionService.check();

    // Lets the search bar land here via a `pluginId` query param and open the details panel, and
    // callers sending users here to install something ask for the Store via `tab=store`.
    // pluginId is beta-gated like displayedPlugins. Both params are cleared straight after:
    // Angular ignores same-URL navigations, so leaving them set makes a repeat link a no-op (this
    // page also stays alive when e.g. the account-creation dialog sends the user here from it).
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ pluginId, tab }) => {
        const plugin = this.plugins.find(
          (p) => p.id === pluginId && (!p.isBetaAccess || this.roleAccessService.isBetaUser()),
        );
        // The caller wants the user to install something: open the Add tools & plugins catalogue.
        if (tab === "store") {
          this.showStore();
        }
        if (!plugin && tab === undefined) {
          return;
        }
        // Deferred to the next microtask so this doesn't fire while the *current* navigation
        // is still activating routes. Starting a second navigation from inside that window
        // supersedes the first, which makes the search bar's `router.navigate(...)` promise
        // resolve `false` on a repeat plugin selection — it then skips hideInput() and
        // handleHistory(), leaving the search overlay open behind this dialog.
        void Promise.resolve().then(() =>
          this.router.navigate([], {
            queryParams: { pluginId: null, tab: null },
            queryParamsHandling: "merge",
            replaceUrl: true,
          }),
        );
        if (plugin) {
          this.openLinkedPlugin(plugin);
        }
      });
  }

  // A linked plugin that isn't installed only appears on the Store tab, so switch there first:
  // otherwise closing its dialog leaves the user on a list that doesn't include it.
  private openLinkedPlugin(plugin: PluginCardData): void {
    void (async () => {
      // isInstalled is snapshotted once and the extension's state is live-detected only.
      if (plugin.id === GlossExtensionService.PLUGIN_ID) {
        await this.extensionService.check();
      }
      if (!this.isPluginInstalled(plugin.id)) {
        this.showStore();
      }
      this.openPluginDetails(plugin);
    })();
  }

  /**
   * User consented via accounts/advanced but has no vault plugin row — backfill config.
   */
  private async syncFiskilPluginConfigFromActiveConsent(): Promise<void> {
    if (this.pluginConfigService.hasEnabledFiskilPluginConfig()) {
      return;
    }

    try {
      const { consents = [] } = await this.fiskilApi.getActiveConsents();
      if (consents.length === 0) {
        return;
      }
      await this.pluginConfigService.ensureFiskilPluginActivated();
    } catch (error) {
      this.log.error(
        "Failed to sync Fiskil plugin config from active consent: " + JSON.stringify(error),
      );
    }
  }

  /**
   * User linked via Plaid but has no vault plugin row — backfill config.
   */
  private async syncPlaidPluginConfigFromActiveItems(): Promise<void> {
    if (this.pluginConfigService.hasEnabledPlaidPluginConfig()) {
      return;
    }

    try {
      const { items = [] } = await this.plaidApi.getItems();
      if (items.length === 0) {
        return;
      }
      await this.pluginConfigService.ensurePlaidPluginActivated();
    } catch (error) {
      this.log.error(
        "Failed to sync Plaid plugin config from active items: " + JSON.stringify(error),
      );
    }
  }

  // Own shallow copies, not PLUGIN_CATALOG's entries directly — onPluginLikeToggle mutates
  // `isLiked` in place, and PLUGIN_CATALOG is a shared module-level singleton also read by the
  // global search bar. Without cloning, liking a plugin here would leak into that shared source
  // and persist across this component's own re-instantiation (route away/back).
  plugins: PluginCardData[] = PLUGIN_CATALOG.filter(
    // The Gloss Vault browser extension can't be installed in the native iOS/Android apps, so hide its
    // card there (its "required for Grocery price comparison" copy is also moot — mobile fetches prices
    // natively). #5542 flipped it to isBetaAccess:false, which otherwise shows it to every mobile user (#5613).
    (plugin) => !(this.isNativeMobile && plugin.id === GlossExtensionService.PLUGIN_ID),
  ).map((plugin) => ({ ...plugin }));

  /** Installed plugin IDs derived from plugin config. "plugin" type plugins plus EODHD ("buildin" type). */
  installedPluginIds = computed(() => {
    // isPluginInstalled() is the single source of truth for every branch (vault config,
    // DATABANK, toggle-only, chatbot), so a new non-vault-backed plugin can't be missed here.
    // Calling it per catalog entry is cheap: the getAllConfigs() it reads is memoised.
    const installed = this.plugins
      .filter((plugin) => this.pluginConfigService.isPluginInstalled(plugin.id))
      .map((p) => p.id);

    // Transfers & Conversions is default-on: its "installed" state is the enabled
    // flag, not a vault config row (its keyless row is invisible to
    // isPluginInstalled). The flag lives in the plugin.collection() signal, so this
    // computed refreshes on its own when the install dialog (or a vault sync) changes it.
    if (this.pluginConfigService.isTransferConversionEnabled()) {
      installed.push(TRANSFER_CONVERSION_PLUGIN_NAME);
    }

    return installed;
  });

  get displayedPlugins(): PluginCardData[] {
    if (this.noFilterOptionSelected) {
      return [];
    }
    let filteredPlugins: PluginCardData[];

    // Filter by tab
    switch (this.selectedTab) {
      case "wishlist":
        filteredPlugins = this.plugins.filter((plugin) => plugin.isLiked);
        break;
      case "my-plugins":
        // Route through isPluginInstalled (not installedPluginIds directly) so the
        // browser extension — whose installed state is live detection, not a
        // data-plugin config — is listed here when detected, consistent with its
        // store-card badge and install dialog.
        filteredPlugins = this.plugins.filter((plugin) => this.isPluginInstalled(plugin.id));
        break;
      case "store":
      default:
        filteredPlugins = this.plugins;
        break;
    }

    // Apply search filter
    if (this.searchQuery && this.searchQuery.trim() !== "") {
      const query = this.searchQuery.toLowerCase().trim();
      filteredPlugins = filteredPlugins.filter(
        (plugin) =>
          plugin.title.toLowerCase().includes(query) ||
          plugin.description.toLowerCase().includes(query) ||
          plugin.type?.toLowerCase().includes(query) ||
          plugin.author.toLowerCase().includes(query),
      );
    }

    if (this.activeTypes.length > 0) {
      filteredPlugins = filteredPlugins.filter((plugin) =>
        this.activeTypes.includes(this.pluginKind(plugin)),
      );
    }

    if (this.activeCategory !== ALL_CATEGORIES) {
      filteredPlugins = filteredPlugins.filter(
        (plugin) => (plugin.type ?? "Other") === this.activeCategory,
      );
    }

    // Apply country filter
    if (this.countriesNarrowed) {
      filteredPlugins = filteredPlugins.filter((plugin) =>
        this.activeCountries.some((c) => (plugin.countries ?? []).includes(c)),
      );
    }

    // Apply bundle filter
    if (this.bundlesNarrowed) {
      filteredPlugins = filteredPlugins.filter((plugin) => {
        if (this.activeBundles.includes("free") && plugin.price === "Free") {
          return true;
        }
        if (
          this.activeBundles.includes("aussie-bank-bundle") &&
          plugin.price === BUNDLE_PRICE_LABEL
        ) {
          return true;
        }
        return false;
      });
    }

    // Beta-only plugins are hidden from non-beta users by *appRoleScope in the template;
    // filter them here too so this list (and its length) reflects what's actually rendered.
    return filteredPlugins.filter(
      (plugin) =>
        (!plugin.isBetaAccess || this.roleAccessService.isBetaUser()) &&
        !this.isInstalledPlugin(plugin),
    );
  }

  /** Tool tiles visible to this user (beta-flagged tools hidden from non-beta users). */
  get visibleToolTiles(): (ToolTile & { plugin: PluginCardData })[] {
    return this.toolPrefs
      .arrange([...this.toolTiles, ...this.mockToolTiles])
      .map((tile) => ({
        tile,
        plugin: this.plugins.find((p) => p.id === tile.id) ?? this.mockPlugin(tile),
      }))
      .filter(
        (entry): entry is { tile: ToolTile; plugin: PluginCardData } =>
          !!entry.plugin && (!entry.plugin.isBetaAccess || this.roleAccessService.isBetaUser()),
      )
      .map(({ tile, plugin }) => ({ ...tile, plugin }));
  }

  private readonly mockToolTiles: ToolTile[] = this.buildMockToolTiles();

  private buildMockToolTiles(): ToolTile[] {
    if (!isDevMode()) {
      return [];
    }
    const count = Math.min(
      Number(localStorage.getItem(MOCK_TOOLS_KEY)) || 0,
      MOCK_TOOL_NAMES.length,
    );
    return MOCK_TOOL_NAMES.slice(0, count).map(([label, icon]) => ({
      id: `mock-${label}`,
      labelKey: "",
      label,
      icon,
    }));
  }

  private mockPlugin(tile: ToolTile): PluginCardData | undefined {
    return tile.label
      ? {
          id: tile.id,
          title: tile.label,
          author: "Mock",
          rating: 0,
          description: "",
          type: "Tools",
        }
      : undefined;
  }

  /** Display name of a tile: translated for real tools, literal for dev mock tiles. */
  tileLabel(tile: ToolTile): string {
    return tile.label ?? this.i18nService.t(tile.labelKey);
  }

  /**
   * Two rows of tiles, where the last slot is always the "Add a tool" tile. The row size follows
   * the width of the grid. On a phone it is a single row of four, with no "Add a tool" tile.
   */
  private get collapsedToolLimit(): number {
    return this.isPhone() ? PHONE_COLLAPSED_TOOL_LIMIT : this.toolColumns * 2 - 1;
  }

  /**
   * Everything the tools section shows, worked out once so the template can read it in one place
   * instead of re-sorting the tiles for every binding.
   */
  get toolsView(): {
    visible: (ToolTile & { plugin: PluginCardData })[];
    displayed: (ToolTile & { plugin: PluginCardData })[];
    canExpand: boolean;
  } {
    const visible = this.visibleToolTiles;
    const canExpand = visible.length > this.collapsedToolLimit;
    const displayed =
      this.toolsExpanded || !canExpand ? visible : visible.slice(0, this.collapsedToolLimit);
    return { visible, displayed, canExpand };
  }

  /** Tiles actually rendered: everything when expanded, else the first two rows. */
  get displayedToolTiles(): (ToolTile & { plugin: PluginCardData })[] {
    return this.toolsView.displayed;
  }

  get canExpandTools(): boolean {
    return this.toolsView.canExpand;
  }

  /** "Add a tool" sits at the end of the grid; on a phone the header + button does that job. */
  get showAddToolTile(): boolean {
    return !this.isPhone();
  }

  togglePhoneFilter(which: "type" | "category" | "price"): void {
    this.phoneFilter = this.phoneFilter === which ? null : which;
  }

  toggleToolsExpanded(): void {
    this.toolsExpanded = !this.toolsExpanded;
  }

  /**
   * Drag-to-reorder. The displayed tiles are a prefix of the full list, so indices line up.
   * Pinned tools always render first, so dragging among them reorders the pins.
   */
  onToolDrop(event: CdkDragDrop<ToolTile[]>): void {
    this.moveTool(event.previousIndex, event.currentIndex);
  }

  /** Keyboard reorder: Alt + arrow keys move the focused tile one slot (same rules as dragging). */
  onToolKeydown(event: KeyboardEvent, index: number): void {
    if (!event.altKey) {
      return;
    }
    const step =
      event.key === "ArrowLeft" || event.key === "ArrowUp"
        ? -1
        : event.key === "ArrowRight" || event.key === "ArrowDown"
          ? 1
          : 0;
    if (step === 0) {
      return;
    }
    event.preventDefault();
    const target = index + step;
    const label = this.tileLabel(this.displayedToolTiles[index]);
    const total = this.visibleToolTiles.length;
    // The collapsed grid shows a prefix of the list, so moving past its last tile has to reveal
    // the next slot or the tile would vanish from under the user's focus.
    const leavesCollapsedGrid = target === this.displayedToolTiles.length && !this.toolsExpanded;
    const moved = target >= 0 && target < total && this.moveTool(index, target);
    if (moved && leavesCollapsedGrid) {
      this.toolsExpanded = true;
    }
    this.toolReorderAnnouncement = moved
      ? this.i18nService.t("pluginsToolMoved", label, (target + 1).toString(), total.toString())
      : this.i18nService.t("pluginsToolCannotMove", label);
    const tile = event.currentTarget as HTMLElement;
    // Moving the node can drop focus; put it back once the grid has re-rendered.
    setTimeout(() => tile.focus());
  }

  /** Back to "most opened first" for the unpinned tools. */
  resetToolOrder(): void {
    this.toolPrefs.setOrder([]);
  }

  /** Returns whether the tool actually moved. */
  private moveTool(from: number, to: number): boolean {
    if (from === to) {
      return false;
    }
    const ids = this.visibleToolTiles.map((t) => t.id);
    const pins = this.toolPrefs.pins();
    const pinnedCount = ids.filter((id) => pins.includes(id)).length;
    // Pinned tools always render first, so a move across the pinned/unpinned boundary would only
    // snap back (and would still rewrite the saved order). Ignore it.
    if (from < pinnedCount !== to < pinnedCount) {
      return false;
    }
    moveItemInArray(ids, from, to);
    if (from < pinnedCount) {
      // Reordering pins leaves the manual order alone. Pins hidden from this user (beta) keep
      // their slots; only the visible pins are re-sequenced around them.
      const reordered = ids.slice(0, pinnedCount);
      const visiblePins = new Set(reordered);
      let next = 0;
      this.toolPrefs.setPins(pins.map((id) => (visiblePins.has(id) ? reordered[next++] : id)));
    } else {
      this.toolPrefs.setOrder(ids);
    }
    return true;
  }

  /** Count badge for an enabled tool's tile ("99+" past 99); 0 hides it. */
  badgeCount(tile: ToolTile): number {
    return this.isPluginInstalled(tile.id) ? this.toolBadges.badgeFor(tile.id) : 0;
  }

  badgeLabel(count: number): string {
    return count > 99 ? "99+" : String(count);
  }

  openEditTools(): void {
    this.dialogFactory.openEditToolsDialog(
      this.visibleToolTiles.map(({ id, labelKey, label, icon }) => ({ id, labelKey, label, icon })),
    );
  }

  /** Matches at the start of any word, so "a" finds "Afterpay" and "Wealth Assistant" alike. */
  private wordMatch(haystack: string, query: string): boolean {
    return ` ${this.searchWords(haystack)}`.includes(` ${query}`);
  }

  /** Lower-cases and turns punctuation into single spaces, so text and query compare alike. */
  private searchWords(text: string): string {
    return text
      .toLowerCase()
      .replace(/[“”"·,:()]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /** Home search: your tools, your plugins and everything still in Add new, in that order. */
  get homeSearch(): HomeSearchResults | null {
    const query = this.homeSearchQuery.trim();
    const k = this.searchWords(query);
    if (!k) {
      return null;
    }
    const tools = this.visibleToolTiles.filter(
      (t) =>
        this.isPluginInstalled(t.id) && this.wordMatch(`${this.tileLabel(t)} ${t.plugin.title}`, k),
    );
    const toolIds = this.toolTiles.map((t) => t.id);
    const plugins = this.installedPluginCards.filter(
      (p) =>
        !toolIds.includes(p.id) && this.wordMatch(`${p.title} ${p.description} ${p.type ?? ""}`, k),
    );
    const notInstalled = this.catalogue.filter(
      (p) =>
        !this.isPluginInstalled(p.id) &&
        this.wordMatch(`${p.title} ${p.type ?? ""} ${p.description}`, k),
    );
    const total = tools.length + plugins.length + notInstalled.length;

    let kind: HomeSearchResults["kind"];
    if (!total) {
      kind = "none";
    } else if (!plugins.length && !notInstalled.length) {
      kind = "tools";
    } else if (!tools.length && !notInstalled.length) {
      kind = "plugins";
    } else if (!tools.length && !plugins.length) {
      kind = "notinst";
    } else {
      kind = "mixed";
    }

    let subKey = "pluginsSearchSubAll";
    if (kind === "none") {
      subKey = "pluginsSearchSubNone";
    } else if (kind === "notinst") {
      subKey =
        notInstalled.length === 1
          ? "pluginsSearchSubOneNotInstalled"
          : notInstalled.length === 2
            ? "pluginsSearchSubTwoNotInstalled"
            : "pluginsSearchSubNoneInstalled";
    } else if (kind === "mixed") {
      subKey = "pluginsSearchSubMixed";
    }

    let noteKey: string | undefined;
    if (kind === "tools") {
      noteKey = "pluginsSearchNoteTools";
    } else if (kind === "plugins") {
      noteKey = "pluginsSearchNotePlugins";
    } else if (kind === "notinst") {
      noteKey = "pluginsSearchNoteNotInstalled";
    } else if (kind === "mixed" && !tools.length) {
      noteKey = "pluginsSearchNoteNoTools";
    }

    return { query, tools, plugins, notInstalled, total, kind, subKey, noteKey };
  }

  /** Installed plugins for the home "Plugins" grid — tools live in "Your tools" instead. */
  get installedPluginCards(): PluginCardData[] {
    const toolIds = this.toolTiles.map((t) => t.id);
    return this.plugins.filter(
      (plugin) =>
        !toolIds.includes(plugin.id) &&
        this.isPluginInstalled(plugin.id) &&
        (!plugin.isBetaAccess || this.roleAccessService.isBetaUser()),
    );
  }

  /**
   * Opens the catalogue fresh. Passing a type opens it with Type already ticked; none lists
   * everything. The home view's buttons pass none until the store has a visible Type control
   * (otherwise the filter can't be seen or unticked).
   */
  showStore(type?: "Tools" | "Plugins", query = ""): void {
    this.clearFilter();
    this.searchQuery = query;
    this.selectedTab = "store";
    this.activeTypes = type ? [type] : [];
    this.view = "store";
  }

  showHome(): void {
    this.view = "home";
  }

  /** Opens the tool if it's enabled; otherwise opens its details so it can be enabled first. */
  openTool(tile: ToolTile & { plugin: PluginCardData }): void {
    if (!this.isPluginInstalled(tile.id)) {
      this.openPluginDetails(tile.plugin);
      return;
    }
    if (tile.label) {
      return; // dev mock tile: nothing to open
    }
    this.toolPrefs.recordOpen(tile.id);
    switch (tile.id) {
      case RECURRING_DETECTOR_PLUGIN_ID:
        void this.router.navigate(["/", GlossRoutes.RecurringDetector]);
        break;
      case GROCERY_PLUGIN_NAME:
        void this.router.navigate(["/", GlossRoutes.GlossGrocery]);
        break;
      case WEALTH_COMPARISON_PLUGIN_NAME:
        void this.dialogFactory.openWealthComparisonDialog();
        break;
    }
  }

  /** Opens the plugin's details/manage dialog (whole card is the click target). */
  openPlugin(plugin: PluginCardData): void {
    this.openPluginDetails(plugin);
  }

  /** Whether the plugin is installed (has a config in plugin config service). */
  isPluginInstalled(pluginId: string): boolean {
    if (this.mockToolTiles.some((tile) => tile.id === pluginId)) {
      return true; // dev mock tile (the list is empty outside dev mode)
    }
    // The browser extension isn't a data-plugin config — its "installed" state
    // is live detection of the extension in the browser.
    if (pluginId === GlossExtensionService.PLUGIN_ID) {
      return this.extensionInstalled();
    }
    return this.installedPluginIds().includes(pluginId);
  }

  onPluginLikeToggle(event: { id: string; isLiked: boolean }): void {
    const plugin = this.plugins.find((p) => p.id === event.id);
    if (plugin) {
      plugin.isLiked = event.isLiked;
    }
  }

  onPluginInstall(event: { id: string; installLink?: string }): void {
    const plugin = this.plugins.find((p) => p.id === event.id);
    if (plugin) {
      this.openPluginDetails(plugin);
    }
  }

  private openPluginDetails(plugin: PluginCardData): void {
    // Use isPluginInstalled (not installedPluginIds directly) so the browser
    // extension — whose installed state is live detection, not a data-plugin
    // config — opens the dialog as "installed" when detected instead of
    // always "Available".
    const isInstalled = this.isPluginInstalled(plugin.id);
    // No onInstallSuccess: installed state is derived entirely from PluginConfigService
    // signals that installedPluginIds tracks (vault configs, toggle-only flags, and the
    // Transfers & Conversions flag in plugin.collection()), so there's no local state to update.
    this.dialogFactory.openPluginDetailsDialog(plugin, isInstalled, undefined, !isInstalled);
  }
}
